import { create } from 'zustand';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

const STORAGE_KEY = 'app-ponto-auth';

export type SessionKind = 'USER' | 'FUNCIONARIO';

export interface SessionPayload {
  accessToken: string | null;
  refreshToken: string | null;
  kind: SessionKind | null;
  funcionarioId?: string | null;
  userId?: string | null;
  empresaId: string | null;
  secretariaId?: string | null;
  dispositivoId?: string | null;
  nome: string | null;
  matricula?: string | null;
}

interface AuthState extends SessionPayload {
  hydrated: boolean;
  setSession: (payload: Partial<SessionPayload>) => Promise<void>;
  clear: () => Promise<void>;
  hydrate: () => Promise<void>;
}

const empty: SessionPayload = {
  accessToken: null,
  refreshToken: null,
  kind: null,
  funcionarioId: null,
  userId: null,
  empresaId: null,
  secretariaId: null,
  dispositivoId: null,
  nome: null,
  matricula: null,
};

async function persist(data: SessionPayload) {
  const json = JSON.stringify(data);
  if (Capacitor.isNativePlatform()) {
    await Preferences.set({ key: STORAGE_KEY, value: json });
  } else {
    try {
      window.localStorage.setItem(STORAGE_KEY, json);
    } catch {
      // ignore quota / private mode
    }
  }
}

async function load(): Promise<SessionPayload | null> {
  try {
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key: STORAGE_KEY });
      return value ? (JSON.parse(value) as SessionPayload) : null;
    }
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SessionPayload) : null;
  } catch {
    return null;
  }
}

async function wipe() {
  if (Capacitor.isNativePlatform()) {
    await Preferences.remove({ key: STORAGE_KEY });
  } else {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ...empty,
  hydrated: false,

  setSession: async (payload) => {
    const next: SessionPayload = { ...empty, ...stripState(get()), ...payload };
    set(next);
    await persist(next);
  },

  clear: async () => {
    set({ ...empty });
    await wipe();
  },

  hydrate: async () => {
    const stored = await load();
    if (stored) {
      set({ ...stored, hydrated: true });
    } else {
      set({ hydrated: true });
    }
  },
}));

function stripState(s: AuthState): SessionPayload {
  const { hydrated: _h, setSession: _ss, clear: _c, hydrate: _hy, ...rest } = s;
  void _h; void _ss; void _c; void _hy;
  return rest;
}

export function selectIsAuthenticated(s: AuthState): boolean {
  return Boolean(s.accessToken);
}
